import { ArrowRight } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { HealthState, ProviderType, type Provider } from '../../gql/types';
import { providerLabel } from '../shared/providerLabels';

// Visual fallback chain for a routing policy (W5). Renders providerOrder left-to-right as
// Primary → Fallback 1 → Fallback 2 …, each hop joined against the live PROVIDERS_QUERY data to
// show the provider's display name + a live health badge (HEALTHY/DEGRADED/DOWN) and its
// enabled/disabled state. A hop whose provider type has NO configured provider renders as a
// visibly inert "ghost" hop ("not configured"), so the fallback semantics read at a glance.

const HEALTH_DOT: Record<HealthState, string> = {
  [HealthState.Healthy]: 'bg-emerald-400',
  [HealthState.Degraded]: 'bg-amber-400',
  [HealthState.Down]: 'bg-rose-400',
};

const HEALTH_LABEL: Record<HealthState, string> = {
  [HealthState.Healthy]: 'Healthy',
  [HealthState.Degraded]: 'Degraded',
  [HealthState.Down]: 'Down',
};

function hopRole(index: number): string {
  return index === 0 ? 'Primary' : `Fallback ${index}`;
}

interface HopProps {
  index: number;
  type: ProviderType;
  provider: Provider | undefined;
}

function Hop({ index, type, provider }: HopProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const role = hopRole(index);
  const configured = provider !== undefined;
  const disabled = configured && !provider.enabled;

  // Ghost hop — the policy references a provider type that has no configured provider.
  if (!configured) {
    return (
      <div
        data-testid="chain-hop"
        data-hop-state="ghost"
        aria-label={`${role}: ${providerLabel(type)} — not configured`}
        className={`flex min-w-[8rem] flex-col gap-1 rounded-xl border border-dashed px-3 py-2 ${
          isDark ? 'border-white/[0.12] bg-transparent' : 'border-slate-300 bg-transparent'
        }`}
      >
        <span
          className={`text-[10px] font-medium uppercase tracking-widest ${
            isDark ? 'text-white/25' : 'text-slate-400'
          }`}
        >
          {role}
        </span>
        <span className={`text-sm font-medium ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
          {providerLabel(type)}
        </span>
        <span className={`text-[11px] italic ${isDark ? 'text-white/25' : 'text-slate-400'}`}>
          not configured
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="chain-hop"
      data-hop-state={disabled ? 'disabled' : 'active'}
      aria-label={`${role}: ${provider.displayName} — ${HEALTH_LABEL[provider.health.state]}${
        disabled ? ', disabled' : ''
      }`}
      className={`flex min-w-[8rem] flex-col gap-1 rounded-xl border px-3 py-2 transition-colors ${
        disabled ? 'opacity-55' : ''
      } ${
        isDark
          ? 'border-white/[0.08] bg-gradient-to-br from-white/[0.05] to-white/[0.02]'
          : 'border-slate-200 bg-white shadow-sm'
      }`}
    >
      <span
        className={`text-[10px] font-medium uppercase tracking-widest ${
          isDark ? 'text-white/30' : 'text-slate-400'
        }`}
      >
        {role}
      </span>
      <span
        className={`truncate text-sm font-semibold ${isDark ? 'text-white/90' : 'text-slate-800'}`}
      >
        {provider.displayName}
      </span>
      <span className="flex items-center gap-1.5">
        <span
          className={`h-2 w-2 flex-shrink-0 rounded-full ${HEALTH_DOT[provider.health.state]}`}
          aria-hidden="true"
        />
        <span className={`text-[11px] ${isDark ? 'text-white/50' : 'text-slate-500'}`}>
          {HEALTH_LABEL[provider.health.state]}
          {disabled && ' · disabled'}
        </span>
      </span>
    </div>
  );
}

export interface FallbackChainProps {
  order: ProviderType[];
  /** Live provider fleet (from PROVIDERS_QUERY) used to resolve each hop's display + health. */
  providers: Provider[];
}

export function FallbackChain({ order, providers }: FallbackChainProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // First configured provider per type wins (weight-ordering is a backend concern; the policy hop
  // names a type, and the board resolves it to its provider for display).
  const byType = new Map<ProviderType, Provider>();
  for (const p of providers) {
    if (!byType.has(p.type)) byType.set(p.type, p);
  }

  if (order.length === 0) {
    return (
      <span className={`text-xs italic ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No providers in this policy.
      </span>
    );
  }

  return (
    <ol
      aria-label="Fallback chain"
      className="flex flex-wrap items-stretch gap-2"
    >
      {order.map((type, index) => (
        <li key={type} className="flex items-center gap-2">
          {index > 0 && (
            <ArrowRight
              className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-white/25' : 'text-slate-300'}`}
              aria-hidden="true"
            />
          )}
          <Hop index={index} type={type} provider={byType.get(type)} />
        </li>
      ))}
    </ol>
  );
}
